import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaPen } from "react-icons/fa";
import { FaPenClip } from "react-icons/fa6";
import {
    LuUserCheck,
    LuUserCog,
    LuUserPen,
    LuUserRound,
    LuChevronDown,
    LuChevronUp
} from "react-icons/lu"
import FormatDateTime from "../Functions/FormatDate";
import { useLanguage } from "../Translations/LenguageContext";

const UserCell = ({ record }) => {
    const initials = `${record.name?.[0] || ""}${record.surname?.[0] || ""}`.toUpperCase();
    return (
        <div className="custom_cell_user">
            <div className="custom_cell_avatar">
                {record.profile_url ? <img src={record.profile_url} alt="" /> : <span>{initials}</span>}
            </div>
            <div className="custom_cell_user_info">
                <span className="custom_cell_name">{record.name} {record.surname}</span>
                <span className="custom_cell_subtext">{record.email}</span>
            </div>
        </div>
    );
};

const OrgListCell = ({ organizations }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const containerRef = useRef(null);

    const checkOverflow = () => {
        const el = containerRef.current;
        if (el) {
            const overflow = el.scrollHeight > 38;
            setHasMore(overflow);
        }
    };

    useLayoutEffect(() => {
        checkOverflow();

        const resizeObserver = new ResizeObserver(() => checkOverflow());
        if (containerRef.current) resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [organizations]);

    if (!organizations || organizations.length === 0) return null;

    return (
        <div className="custom_cell_org_wrapper">

            <div
                ref={containerRef}
                className={`custom_cell_org_list ${isExpanded ? 'custom_cell_org_list_expanded' : ''}`}
            >
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="org_list_toggle"
                        type="button"
                    >
                        {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                    </button>
                )}
                {organizations.map((org) => (
                    <div key={org.id} className="custom_cell_org_pill">
                        <span className="org_pill_name" title={org.name}>{org.name}</span>
                        <span className="role_badge" title={org.role}>
                            {org.role === "owner" && <i><LuUserCheck /></i>}
                            {org.role === "manager" && <i><LuUserCog /></i>}
                            {org.role === "developer" && <i><LuUserPen /></i>}
                            {org.role === "member" && <i><LuUserRound /></i>}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StatusCell = ({ status }) => {
    const status_text = status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "Active";

    return (
        <div className="custom_cell_status">
            <span className={`status_dot ${status?.toLowerCase() || 'active'}`}></span>
            <span className="status_text">{status_text}</span>
        </div>
    );
};

const MemberTable = ({ type, data, handleOpenModalUpdate, userData }) => {
    const {t} = useLanguage()
    
    const headers = useMemo(() => {
        if (type === "members") return [t.members.user, t.members.org_roles, "Status", ""];
        return [];
    }, [type]);

    return (
        <div className="custom_table_container fade-in">
            <table className="custom_table">
                {headers.map((header, index) => (
                    <th key={index} className="custom_table_th">
                        {header}
                    </th>
                ))}
                <tbody className="custom_table_body">
                    {data.length > 0 ? (
                        data.map((item, rowIndex) => (
                            <tr key={item.id || rowIndex} className="custom_table_row">
                                <td className="custom_table_td td_user">
                                    <UserCell record={item} />
                                </td>
                                <td className="custom_table_td td_orgs">
                                    <OrgListCell organizations={item.organizations} />
                                </td>
                                <td className="custom_table_td td_status">
                                    <StatusCell status={item.status} />
                                </td>
                                <td className="custom_table_td td_status" >
                                    {userData.email != item.email && (
                                        <i className="custom_table_td_icon" onClick={() => handleOpenModalUpdate(item)}><FaPen /></i>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr className="custom_table_row_empty">
                            <td colSpan={headers.length} className="custom_table_td_empty">
                                {t.no_data_found_by_search}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default MemberTable;